import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ThemeProvider } from "./context/ThemeContext";
import { BillingProvider } from "./context/BillingContext";
import { AppShell } from "./components/AppShell";
import Login from "./pages/Login";
import ChangePassword from "./pages/ChangePassword";
import Dashboard from "./pages/Dashboard";
import Approvals from "./pages/Approvals";
import Projects from "./pages/Projects";
import ProjectDetail from "./pages/ProjectDetail";
import Board from "./pages/Board";
import TaskDetail from "./pages/TaskDetail";
import NewTask from "./pages/NewTask";
import Feed from "./pages/Feed";
import Monitoring from "./pages/Monitoring";
import {
  SeoOverview, SeoReports, SeoKeywords, SeoWorklog,
  MarketingQueue, MarketingPlans, MarketingGallery,
} from "./pages/hubs/Hubs";
import Costs from "./pages/Costs";
import Settings from "./pages/Settings";
import Chat from "./pages/Chat";
import Inbound from "./pages/Inbound";
import Opportunities from "./pages/Opportunities";
import Floor from "./pages/Floor";
import Social from "./pages/Social";
import Afg from "./pages/Afg";

function Shell({ children }) {
  return <AppShell>{children}</AppShell>;
}

function Protected({ children, bypassMustChange = false }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center text-muted-foreground">
        Loading…
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }
  if (user.must_change_password && !bypassMustChange) {
    return <Navigate to="/change-password" replace />;
  }
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/change-password"
        element={
          <Protected bypassMustChange>
            <ChangePassword />
          </Protected>
        }
      />
      <Route path="/" element={<Protected><Shell><Dashboard /></Shell></Protected>} />
      <Route path="/floor" element={<Protected><Shell><Floor /></Shell></Protected>} />
      <Route path="/chat" element={<Protected><Shell><Chat /></Shell></Protected>} />
      <Route path="/inbound" element={<Protected><Shell><Inbound /></Shell></Protected>} />
      <Route path="/opportunities" element={<Protected><Shell><Opportunities /></Shell></Protected>} />
      <Route path="/approvals" element={<Protected><Shell><Approvals /></Shell></Protected>} />
      <Route path="/projects" element={<Protected><Shell><Projects /></Shell></Protected>} />
      <Route path="/projects/:slug" element={<Protected><Shell><ProjectDetail /></Shell></Protected>} />
      <Route path="/board" element={<Protected><Shell><Board /></Shell></Protected>} />
      <Route path="/tasks/new" element={<Protected><Shell><NewTask /></Shell></Protected>} />
      <Route path="/tasks/:id" element={<Protected><Shell><TaskDetail /></Shell></Protected>} />
      <Route path="/feed" element={<Protected><Shell><Feed /></Shell></Protected>} />
      <Route path="/monitoring" element={<Protected><Shell><Monitoring /></Shell></Protected>} />
      <Route path="/seo"           element={<Protected><Shell><SeoOverview /></Shell></Protected>} />
      <Route path="/seo/reports"   element={<Protected><Shell><SeoReports /></Shell></Protected>} />
      <Route path="/seo/keywords"  element={<Protected><Shell><SeoKeywords /></Shell></Protected>} />
      <Route path="/seo/worklog"   element={<Protected><Shell><SeoWorklog /></Shell></Protected>} />
      <Route path="/marketing"          element={<Protected><Shell><MarketingQueue /></Shell></Protected>} />
      <Route path="/marketing/plans"    element={<Protected><Shell><MarketingPlans /></Shell></Protected>} />
      <Route path="/marketing/gallery"  element={<Protected><Shell><MarketingGallery /></Shell></Protected>} />
      <Route path="/afg" element={<Protected><Shell><Afg /></Shell></Protected>} />
      <Route path="/social" element={<Protected><Shell><Social /></Shell></Protected>} />
      <Route path="/costs" element={<Protected><Shell><Costs /></Shell></Protected>} />
      <Route path="/settings" element={<Protected><Shell><Settings /></Shell></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BillingProvider>
          <BrowserRouter>
            <AppRoutes />
            <Toaster position="top-right" richColors closeButton />
          </BrowserRouter>
        </BillingProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}
